import React, { useState, useEffect } from 'react';
import {
  LayoutDashboard, ListChecks, Package, AlertTriangle,
  Share2, FileText, BarChart3, Settings,
  ShieldCheck, Shield, Wrench, ScanSearch,
  ChevronLeft, ChevronRight, Menu, X, Folder, Sparkles,
  Flame, GitBranch, Code, Globe, Lock, Brain, Server, Cloud,
  Crown, Workflow, Network, ScrollText, Zap,
  Database, Boxes, Bug, Radar, Target, Fingerprint, Search,
} from 'lucide-react';
import { useSecurityEvents } from '../../hooks/useSecurityEvents';
import { apiClient } from '../../services/api/client';
import { getActiveProjectContext, setActiveProjectContext, clearActiveProjectContext } from '../../shared/utils/projectContext';

interface NavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path: string;
}

interface NavSection {
  label: string;
  items: NavItem[];
}

interface SidebarProps {
  currentPath: string;
  onNavigate: (path: string) => void;
  isOpen?: boolean;
  onClose?: () => void;
}

// Wiz-style sidebar. Top-level primitives (Monitor/Investigate) plus the
// CNAPP security pillars as first-class nav nodes — matching Wiz's real left
// rail, which exposes Vulnerabilities/Identity/Data/Compute/Containers as
// dedicated entries rather than burying them as filter chips. Every path
// below maps to a fully-built dashboard; previously ~40 of these were only
// reachable via the command palette or a hand-typed URL. Connectors / Audit
// Logs / Controls remain sub-pages of Settings.
const NAV_SECTIONS: NavSection[] = [
  {
    label: 'Monitor',
    items: [
      { id: 'overview',   label: 'Dashboard',  icon: <LayoutDashboard />, path: '/overview' },
      { id: 'issues',     label: 'Issues',     icon: <AlertTriangle />,   path: '/issues' },
      { id: 'findings',   label: 'Findings',   icon: <ScanSearch />,      path: '/findings' },
      { id: 'risk-queue', label: 'Risk Queue', icon: <Flame />,           path: '/risk-queue' },
    ],
  },
  {
    label: 'Investigate',
    items: [
      { id: 'graph',          label: 'Security Graph', icon: <Share2 />,        path: '/graph' },
      // /query (NLQueryPanel) is a registered route in App.tsx with no sidebar
      // entry, no palette entry and no in-app link — reachable only by typing
      // the URL. Asking the inventory a question in plain language is a
      // headline surface; it was shipped unreachable.
      { id: 'query',          label: 'Ask the Graph',  icon: <Search />,        path: '/query' },
      { id: 'inventory',      label: 'Inventory',      icon: <Package />,       path: '/inventory' },
      { id: 'attack-paths',   label: 'Attack Paths',   icon: <Network />,       path: '/attack-paths' },
      { id: 'threats',        label: 'Threats',        icon: <AlertTriangle />, path: '/threats' },
      { id: 'investigations', label: 'Investigations', icon: <Search />,        path: '/investigations' },
      { id: 'event-scanner',  label: 'Event Scanner',  icon: <Zap />,           path: '/event-scanner' },
      { id: 'projects',       label: 'Projects',       icon: <Folder />,        path: '/projects' },
    ],
  },
  {
    label: 'Cloud Security',
    items: [
      { id: 'cspm',          label: 'Cloud Posture',    icon: <Cloud />,       path: '/cspm' },
      { id: 'ciem',          label: 'Identity (CIEM)',  icon: <Fingerprint />, path: '/ciem' },
      { id: 'dspm',          label: 'Data (DSPM)',      icon: <Database />,    path: '/dspm' },
      { id: 'cwpp',          label: 'Workloads (CWPP)', icon: <Server />,      path: '/cwpp' },
      { id: 'kspm',          label: 'Kubernetes',       icon: <Boxes />,       path: '/kspm' },
      { id: 'container-k8s', label: 'Containers',       icon: <Boxes />,       path: '/container-k8s' },
      { id: 'vulnerabilities', label: 'Vulnerabilities', icon: <Bug />,        path: '/vulnerabilities' },
      { id: 'ai-spm',        label: 'AI Security',      icon: <Brain />,       path: '/ai-spm' },
    ],
  },
  {
    label: 'Exposure',
    items: [
      { id: 'easm',     label: 'Attack Surface', icon: <Radar />,  path: '/easm' },
      { id: 'exposure', label: 'Exposure',       icon: <Target />, path: '/exposure' },
    ],
  },
  {
    label: 'Application Security',
    items: [
      { id: 'code-security', label: 'Code Security', icon: <Code />,      path: '/code-security' },
      { id: 'supply-chain',  label: 'Supply Chain',  icon: <GitBranch />, path: '/supply-chain' },
    ],
  },
  {
    label: 'Respond',
    items: [
      { id: 'remediation',     label: 'Remediation',     icon: <Wrench />,     path: '/remediation' },
      { id: 'workflows',       label: 'Workflows',       icon: <Workflow />,   path: '/workflows' },
      { id: 'detection-rules', label: 'Detection Rules', icon: <ScrollText />, path: '/detection-rules' },
    ],
  },
  {
    label: 'Govern',
    items: [
      { id: 'compliance',       label: 'Compliance',       icon: <ShieldCheck />, path: '/compliance' },
      { id: 'posture-policies', label: 'Posture Policies', icon: <ShieldCheck />, path: '/posture-policies' },
      { id: 'policies',         label: 'Policies',         icon: <Shield />,      path: '/policies' },
      { id: 'reports',          label: 'Reports',          icon: <BarChart3 />,   path: '/reports' },
    ],
  },
  // Settings is NOT a nav section — it duplicated the bottom-pinned Settings
  // button (the Wiz pattern). Keep only the pinned one at the rail bottom.
  // R193-SHIP-O 2026-05-19 — per-tenant blast-radius admin view
  // (Wiz multi-tenant operator dashboard).
  {
    label: 'Admin',
    items: [
      { id: 'tenant-blast-radius', label: 'Tenant Blast Radius', icon: <Crown />,
        path: '/admin/tenants/blast-radius' },
    ],
  },
];

/** Mobile hamburger button — rendered outside sidebar for small screens */
export const MobileMenuButton: React.FC<{ onClick: () => void; isOpen: boolean }> = ({ onClick, isOpen }) => (
  <button
    onClick={onClick}
    className="md:hidden fixed top-3 left-3 z-50 p-2 rounded-lg bg-wiz-dark border border-wiz-border text-gray-400 hover:text-white transition-colors"
    aria-label={isOpen ? 'Close navigation menu' : 'Open navigation menu'}
  >
    {isOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
  </button>
);

const Sidebar: React.FC<SidebarProps> = ({ currentPath, onNavigate, isOpen, onClose }) => {
  const { isConnected, latest } = useSecurityEvents();
  // Wiz defaults to the LABELED nav (section headers + item labels), not a
  // mystery-meat icon rail. Default expanded unless the user explicitly
  // collapsed it (stored 'false'); an unset key → expanded.
  // Below ~1280px the expanded rail (208px) plus a dense table pushes the last
  // columns off-screen with no affordance — Wiz collapses to the icon rail at
  // narrow widths. An explicit user choice still wins at wide widths; the
  // auto-collapse only applies while the viewport is narrow and is never
  // persisted, so widening the window restores the user's own preference.
  const NARROW_PX = 1280;
  const userPref = () => localStorage.getItem('sidebar_expanded') !== 'false';
  const [expanded, setExpanded] = useState(() => (
    typeof window !== 'undefined' && window.innerWidth < NARROW_PX ? false : userPref()
  ));
  useEffect(() => {
    const onResize = () => {
      setExpanded(window.innerWidth < NARROW_PX ? false : userPref());
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const [unseenIssues, setUnseenIssues] = useState(0);
  const [unseenFindings, setUnseenFindings] = useState(0);

  // NOTE: we deliberately do NOT persist `expanded` in a mount effect. The old
  // code wrote 'false' on first render before the user touched anything, which
  // polluted the "explicit choice" signal and defeated the expanded default.
  // Persistence now happens only inside the toggle handler (explicit intent).
  const toggleExpanded = () => setExpanded(prev => {
    const next = !prev;
    localStorage.setItem('sidebar_expanded', String(next));
    return next;
  });

  useEffect(() => {
    if (!latest) return;
    if (latest.type === 'issue.created') {
      if (currentPath !== '/issues') setUnseenIssues(n => n + 1);
      if (currentPath !== '/findings') setUnseenFindings(n => n + 1);
    }
  }, [latest, currentPath]);

  useEffect(() => {
    if (currentPath === '/issues') setUnseenIssues(0);
    if (currentPath === '/findings') setUnseenFindings(0);
  }, [currentPath]);

  const getBadge = (id: string): number | null => {
    if (id === 'issues' && unseenIssues > 0) return unseenIssues;
    if (id === 'findings' && unseenFindings > 0) return unseenFindings;
    return null;
  };

  const getBadgeColor = (id: string): string => {
    if (id === 'issues') return 'bg-red-500';
    if (id === 'findings') return 'bg-orange-500';
    return 'bg-wiz-purple';
  };

  const renderItem = (item: NavItem) => {
    const active = currentPath === item.path ||
      (item.path === '/overview' && currentPath === '/');
    const badge = getBadge(item.id);

    return (
      <button
        key={item.id}
        onClick={() => handleNav(item.path)}
        title={!expanded ? item.label : undefined}
        aria-label={`Navigate to ${item.label}${badge ? `, ${badge} new` : ''}`}
        aria-current={active ? 'page' : undefined}
        className={`
          group relative w-full flex items-center gap-2.5
          ${expanded ? 'px-3 py-2' : 'flex-col justify-center py-2 px-1'}
          rounded-lg text-center transition-all duration-200
          focus:outline-none focus-visible:ring-2 focus-visible:ring-wiz-purple/50
          ${active
            ? 'bg-wiz-purple/10 text-wiz-purple'
            : 'text-gray-500 hover:text-gray-300 hover:bg-wiz-card/60'
          }
        `}
      >
        {/* Active indicator bar */}
        {active && <span className="nav-active-bar" aria-hidden="true" />}

        {/* Icon */}
        <span className="relative w-5 h-5 shrink-0 [&>svg]:w-full [&>svg]:h-full" aria-hidden="true">
          {item.icon}
          {badge && !expanded && (
            <span className={`absolute -top-1 -right-1 min-w-[14px] h-3.5 flex items-center justify-center
              ${getBadgeColor(item.id)} text-white text-xs font-bold rounded-full px-0.5 leading-none z-10`}
              aria-hidden="true">
              {badge > 9 ? '9+' : badge}
            </span>
          )}
        </span>

        {/* Label — expanded only. Collapsed rail is icon-only (Wiz pattern);
            the label surfaces via the hover tooltip below, so no truncated
            text under the icon. */}
        {expanded && (
          <span className={`text-[13px] font-medium leading-none truncate flex-1 text-left ${active ? 'text-wiz-purple' : ''}`}>
            {item.label}
          </span>
        )}

        {/* Badge (expanded mode) */}
        {badge && expanded && (
          <span className={`${getBadgeColor(item.id)} text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1`}>
            {badge > 99 ? '99+' : badge}
          </span>
        )}

        {/* Tooltip (collapsed mode only) */}
        {!expanded && (
          <span className="
            pointer-events-none absolute left-[calc(100%+8px)] top-1/2 -translate-y-1/2
            bg-gray-900 border border-wiz-border text-gray-300 text-xs px-2.5 py-1.5 rounded-lg
            shadow-xl whitespace-nowrap opacity-0 group-hover:opacity-100
            transition-opacity duration-150 z-50
          " role="tooltip">
            {item.label}
          </span>
        )}
      </button>
    );
  };

  // Close mobile menu on navigation
  const handleNav = (path: string) => {
    onNavigate(path);
    if (onClose) onClose();
  };

  return (
    <>
      {/* Mobile overlay backdrop */}
      {isOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/60 z-40 transition-opacity"
          onClick={onClose}
          aria-hidden="true"
        />
      )}
      <aside
        className={`
          ${expanded ? 'sidebar-expanded' : 'sidebar-collapsed'}
          shrink-0 flex flex-col h-screen bg-wiz-dark border-r border-wiz-border select-none
          md:relative md:translate-x-0
          ${isOpen ? 'fixed inset-y-0 left-0 z-50 translate-x-0 sidebar-expanded' : 'hidden md:flex'}
          transition-transform duration-200
        `}
        role="complementary"
        aria-label="Sidebar navigation"
      >
      {/* Expand/Collapse toggle */}
      <div className={`flex ${expanded ? 'justify-end' : 'justify-center'} px-2 pt-2`}>
        <button
          onClick={toggleExpanded}
          className="p-1 text-gray-600 hover:text-gray-400 rounded-md hover:bg-wiz-card/50 transition-colors"
          title={expanded ? 'Collapse sidebar' : 'Expand sidebar'}
          aria-label={expanded ? 'Collapse sidebar' : 'Expand sidebar'}
        >
          {expanded ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
      </div>

      {/* R82 — Project Scope switcher (Wiz-parity sidebar pattern) */}
      <ProjectSwitcher expanded={expanded} />


      <nav className="flex-1 py-1 px-1.5 flex flex-col gap-0.5 overflow-y-auto
        scrollbar-thin scrollbar-thumb-wiz-border scrollbar-track-transparent"
        role="navigation" aria-label="Main navigation">

        {NAV_SECTIONS.map((section, idx) => (
          <div key={section.label} role="group" aria-label={section.label}>
            {/* Section divider (skip first) */}
            {idx > 0 && <div className="nav-section-divider" aria-hidden="true" />}

            {/* Section label (expanded only) */}
            {expanded && (
              <div className="nav-section-label" aria-hidden="true">
                {section.label}
              </div>
            )}

            {section.items.map(renderItem)}
          </div>
        ))}
      </nav>

      {/* Live stream status dot */}
      <div className={`flex ${expanded ? 'items-center gap-2 px-3' : 'justify-center'} py-1.5`}
        title={isConnected ? 'Real-time event stream connected' : 'Real-time stream disconnected'}
        role="status"
        aria-label={isConnected ? 'Real-time event stream connected' : 'Real-time stream disconnected'}>
        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${isConnected ? 'bg-green-500 animate-pulse' : 'bg-gray-600'}`} aria-hidden="true" />
        {expanded && (
          <span className="text-xs text-gray-600">{isConnected ? 'Live' : 'Disconnected'}</span>
        )}
      </div>

      {/* Bottom: Settings */}
      <div className="border-t border-wiz-border px-1.5 py-2">
        <button
          onClick={() => handleNav('/settings')}
          title={!expanded ? 'Settings' : undefined}
          aria-label="Navigate to Settings"
          aria-current={currentPath === '/settings' ? 'page' : undefined}
          className={`
            relative flex items-center gap-2.5 w-full rounded-lg
            transition-colors duration-200
            ${expanded ? 'px-3 py-2' : 'flex-col justify-center py-2 px-1'}
            ${currentPath === '/settings'
              ? 'text-wiz-purple bg-wiz-purple/10'
              : 'text-gray-500 hover:text-gray-300 hover:bg-wiz-card/60'}
          `}
        >
          {currentPath === '/settings' && <span className="nav-active-bar" aria-hidden="true" />}
          <Settings className="w-5 h-5 shrink-0" aria-hidden="true" />
          {expanded ? (
            <span className="text-[13px] font-medium leading-none">Settings</span>
          ) : (
            <span className="text-xs font-medium leading-none" aria-hidden="true">Settings</span>
          )}
        </button>
      </div>
      </aside>
    </>
  );
};

// ─── R82 — Project Scope Switcher ────────────────────────────────────────
const ProjectSwitcher: React.FC<{ expanded: boolean }> = ({ expanded }) => {
  const [active, setActive] = useState(() => getActiveProjectContext());
  const [projects, setProjects] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const loadProjects = async () => {
    if (loaded) return;
    try {
      const { data } = await apiClient.get<any>('/api/v1/projects', { limit: 100 });
      const list = (data as any)?.projects || (data as any)?.items || (Array.isArray(data) ? data : []);
      setProjects(list);
      setLoaded(true);
    } catch {
      setProjects([]);
      setLoaded(true);
    }
  };

  useEffect(() => {
    if (open) loadProjects();
  }, [open]);

  const pickProject = (p: any) => {
    const ctx = {
      id: String(p.id),
      name: p.name,
      tags: (p.tags || {}) as Record<string, string>,
    };
    setActiveProjectContext(ctx);
    setActive(ctx);
    setOpen(false);
    // Force pages to re-read scope by signalling a synthetic storage event.
    try {
      window.dispatchEvent(new StorageEvent('storage', { key: 'active_project_context' }));
    } catch { /* SSR / old browsers — banner-on-mount path still works */ }
  };

  const clearProject = () => {
    clearActiveProjectContext();
    setActive(null);
    setOpen(false);
    try { window.dispatchEvent(new StorageEvent('storage', { key: 'active_project_context' })); } catch {}
  };

  if (!expanded) {
    // Collapsed: small folder icon with dot indicator
    return (
      <button
        onClick={() => setOpen(prev => !prev)}
        title={active ? `Project: ${active.name}` : 'No project filter'}
        className="relative mx-auto mt-2 mb-1 p-1.5 text-gray-500 hover:text-wiz-purple rounded-md hover:bg-wiz-card/60 transition-colors"
      >
        <Folder className="w-5 h-5" />
        {active && <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-wiz-purple rounded-full" />}
      </button>
    );
  }

  return (
    <div className="px-2 pt-2 pb-1 relative">
      <button
        onClick={() => setOpen(prev => !prev)}
        className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md border text-xs transition-colors ${active ? 'bg-wiz-purple/10 border-wiz-purple/40 text-wiz-purple' : 'bg-wiz-card border-wiz-border text-gray-400 hover:text-white'}`}
        title={active ? `Filtering by project: ${active.name}` : 'Click to filter by project'}
      >
        <Folder className="w-3.5 h-3.5 shrink-0" />
        <span className="flex-1 text-left truncate">{active ? active.name : 'All projects'}</span>
        <ChevronRight className={`w-3 h-3 shrink-0 transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>
      {open && (
        <div className="absolute left-2 right-2 top-full mt-1 z-50 bg-wiz-dark border border-wiz-border rounded-lg shadow-xl max-h-72 overflow-y-auto">
          {active && (
            <button
              onClick={clearProject}
              className="w-full text-left px-3 py-1.5 text-xs text-gray-400 hover:bg-wiz-card hover:text-white border-b border-wiz-border"
            >
              ✕ Clear project filter
            </button>
          )}
          {!loaded ? (
            <div className="px-3 py-2 text-xs text-gray-500">Loading...</div>
          ) : projects.length === 0 ? (
            <div className="px-3 py-2 text-xs text-gray-500">No projects defined</div>
          ) : (
            projects.map((p: any) => (
              <button
                key={p.id}
                onClick={() => pickProject(p)}
                className={`w-full text-left px-3 py-1.5 text-xs hover:bg-wiz-card transition-colors ${active?.id === String(p.id) ? 'text-wiz-purple bg-wiz-purple/5' : 'text-gray-300'}`}
              >
                <div className="flex items-center gap-2">
                  <span className="flex-1 truncate">{p.name}</span>
                  {p.resource_count != null && <span className="text-gray-500 text-xs">{p.resource_count}</span>}
                </div>
                {p.description && <p className="text-xs text-gray-500 truncate mt-0.5">{p.description}</p>}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default Sidebar;
